import { ExpressTier } from "data-types";

export type ResolvedExpressShipping = {
  tiersToShow: ExpressTier[];
  showDhl: boolean;
};

/**
 * Decide which express tiers to show on the Shipping page and whether DHL also shows.
 *
 * `availableExpressTiers` comes from the express quote API — every tier eligible for the
 * customer's zip (the quote fans out across all enabled providers, so this reflects pure
 * eligibility, not the cart's selection).
 *
 * Uber is surfaced when EITHER:
 *   - the customer chose Uber in choose-treatment (`choseUber`), or
 *   - the zip is Uber-eligible but NOT Urbify-eligible (Uber is the only express provider).
 * The second clause is independent of the cart's (mutable) shipping_company_code, so
 * switching to DHL no longer drops the Uber option for Uber-only zips. When the zip has
 * Urbify coverage and the customer didn't choose Uber, Uber stays hidden.
 *
 * Tier preference for the single Urbify slot: same_day (before cutoff) over next_day,
 * matching the original DeliveryOptionPanel logic.
 *
 * `ignoreAbTestGate` bypasses the `abtest_urbify` localStorage check entirely and treats
 * Urbify as shown whenever it's eligible. Used for RX once Urbify is fully rolled out
 * (HL-10487), where the localStorage flag set at choose-treatment can't be relied on.
 */
export const resolveExpressShippingTiers = (
  availableExpressTiers: ExpressTier[],
  options?: { ignoreAbTestGate?: boolean },
): ResolvedExpressShipping => {
  const uberTier = availableExpressTiers.find(t => t.tier === "uber_2h" && t.isAvailable);

  const isUrbifyFlow = options?.ignoreAbTestGate || localStorage.getItem("abtest_urbify") === "Show";
  const bestUrbifyTier =
    availableExpressTiers.find(t => t.tier === "same_day" && !t.isCutoffPassed) ||
    availableExpressTiers.find(t => t.tier === "next_day" && t.isAvailable);

  if (isUrbifyFlow && uberTier && bestUrbifyTier) {
    return { tiersToShow: [bestUrbifyTier, uberTier], showDhl: true };
  }
  if (isUrbifyFlow && bestUrbifyTier) {
    return { tiersToShow: [bestUrbifyTier], showDhl: true };
  }
  return { tiersToShow: uberTier ? [uberTier] : [], showDhl: true };
};
